import { Link } from "react-router-dom";
import { Facebook, Instagram, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SeoFooterGrid } from "@/components/SeoFooterGrid";
import { useLocale } from "@/contexts/LocaleContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import travelBoostLogo from "@/assets/travel-boost-logo.jpg";

export const Footer = () => {
  const { t } = useTranslation();
  const { isRTL } = useLocale();

  const { data: footerSettings } = useQuery({
    queryKey: ["footer-settings"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "footer_settings")
        .single();
      return data?.value as { facebook?: string; instagram?: string; whatsapp?: string; description?: string } | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const facebookUrl = footerSettings?.facebook || "https://www.facebook.com/profile.php?id=61588075077612";
  const instagramUrl = footerSettings?.instagram || "https://instagram.com";
  const whatsappUrl = footerSettings?.whatsapp || "https://wa.me/972501234567";

  return (
    <>
      <SeoFooterGrid />
      <footer className="bg-slate-900 text-white pb-20 md:pb-0" dir={isRTL ? "rtl" : "ltr"} role="contentinfo">
      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          
          {/* Column 1: Brand & Bio */}
          <div className="space-y-4">
            <img src={travelBoostLogo} alt="Travel Boost" className="h-28 w-auto object-contain brightness-0 invert" />
            <p className="text-slate-300 text-sm leading-relaxed">
              {footerSettings?.description || t("footer.brand_desc")}
            </p>
            <nav aria-label="social" className="flex items-center gap-3 pt-2">
              <a 
                href={facebookUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 rounded-full bg-slate-800 flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href={instagramUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 rounded-full bg-slate-800 flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" aria-hidden="true" />
              </a>
              <a 
                href={whatsappUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-11 w-11 rounded-full bg-slate-800 flex items-center justify-center hover:bg-green-500 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle className="h-5 w-5" aria-hidden="true" />
              </a>
            </nav>
          </div>

          {/* Column 2: Quick Links */}
          <nav className="space-y-4" aria-label="quick nav">
            <h3 className="text-lg font-bold">{t("footer.quick_nav")}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/destinations/israel" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.destinations_israel")}
                </Link>
              </li>
              <li>
                <Link to="/destinations/world" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.destinations_world")}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.blog")}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.contact")}
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 3: Services */}
          <nav className="space-y-4" aria-label="services">
            <h3 className="text-lg font-bold">{t("footer.services")}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/hotels" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.hotel_booking")}
                </Link>
              </li>
              <li>
                <Link to="/cars" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.car_rental")}
                </Link>
              </li>
              <li>
                <Link to="/insurance" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.travel_insurance")}
                </Link>
              </li>
              <li>
                <Link to="/esim" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  eSIM
                </Link>
              </li>
            </ul>
          </nav>

          {/* Column 4: Legal & Trust */}
          <nav className="space-y-4" aria-label="legal">
            <h3 className="text-lg font-bold">{t("footer.important_info")}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <Link to="/accessibility" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.accessibility")}
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-300 hover:text-white transition-colors text-sm min-h-[44px] inline-block py-1">
                  {t("footer.privacy")}
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-10 pt-6">
          <p className="text-center text-slate-400 text-sm">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
    </>
  );
};
